import React, { useState } from 'react';
import { paymentService } from '../services/payments';
import Alert from './Common/Alert';

const BankDetailsModal = ({ show, onClose, onSuccess, existingDetails }) => {
  const [formData, setFormData] = useState({
    account_holder_name: existingDetails?.account_holder_name || '',
    bank_name: existingDetails?.bank_name || '',
    bank_account_number: '',
    confirm_account_number: '',
    bank_ifsc_code: existingDetails?.ifsc_code || '',
    confirm_ifsc_code: '',
    mobile_number: existingDetails?.mobile_number || ''
  });
  const [alert, setAlert] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convert IFSC codes to uppercase automatically
    if (name === 'bank_ifsc_code' || name === 'confirm_ifsc_code') {
      processedValue = value.toUpperCase();
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    
    // Validation
    if (formData.bank_account_number !== formData.confirm_account_number) {
      setAlert({
        type: 'danger',
        message: 'Account numbers do not match'
      });
      return;
    }
    
    if (formData.bank_ifsc_code.toUpperCase() !== formData.confirm_ifsc_code.toUpperCase()) {
      setAlert({
        type: 'danger',
        message: 'IFSC codes do not match'
      });
      return;
    }
    
    if (!/^[0-9]{10}$/.test(formData.mobile_number)) {
      setAlert({
        type: 'danger',
        message: 'Mobile number must be 10 digits'
      });
      return;
    }
    
    // Basic IFSC validation
    if (formData.bank_ifsc_code.length < 8 || formData.bank_ifsc_code.length > 15) {
      setAlert({
        type: 'danger',
        message: 'IFSC code must be between 8-15 characters'
      });
      return;
    }
    
    try {
      setSubmitting(true);
      await paymentService.updateBankDetails(formData);
      setAlert({
        type: 'success',
        message: 'Bank details updated successfully'
      });
      if (onSuccess) onSuccess();
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (error) {
      console.error('Error updating bank details:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to update bank details. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }} onClick={onClose}>
      <div className="rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#0a0f1e' }} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 flex justify-between items-center" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
          <div>
            <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-3">
              <i className="bi bi-bank" style={{ color: '#ef4444' }}></i>
              Bank Details
            </h2>
            <p className="text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>Update your bank account information</p>
          </div>
          <button
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#64748b' }}
            onClick={onClose}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {alert && (
            <div className="mb-6">
              <Alert
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(null)}
              />
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {/* Account Holder & Bank Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                  Account Holder Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleInputChange}
                  placeholder="Enter account holder name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                  Bank Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="Enter bank name"
                  required
                />
              </div>
            </div>

            {/* Account Numbers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                  Account Number *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg text-sm font-mono outline-none"
                  style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                  name="bank_account_number"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Enter account number"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                  Confirm Account Number *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg text-sm font-mono outline-none"
                  style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                  name="confirm_account_number"
                  value={formData.confirm_account_number}
                  onChange={handleInputChange}
                  placeholder="Re-enter account number"
                  required
                />
              </div>
            </div>

            {/* IFSC Codes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                  IFSC Code *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg text-sm font-mono outline-none uppercase"
                  style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                  name="bank_ifsc_code"
                  value={formData.bank_ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC code"
                  maxLength="11"
                  required
                />
                <p className="text-xs mt-1" style={{ color: '#64748b' }}>11 characters (auto-uppercase)</p>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                  Confirm IFSC Code *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg text-sm font-mono outline-none uppercase"
                  style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                  name="confirm_ifsc_code"
                  value={formData.confirm_ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Re-enter IFSC code"
                  maxLength="11"
                  required
                />
              </div>
            </div>

            {/* Mobile Number */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                Mobile Number *
              </label>
              <input
                type="tel"
                className="w-full px-4 py-3 rounded-lg text-sm font-mono outline-none"
                style={{ backgroundColor: '#161e35', color: '#f8fafc', border: 'none' }}
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
                pattern="[0-9]{10}"
                required
              />
              <p className="text-xs mt-1" style={{ color: '#64748b' }}>10 digits only</p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <button
                type="button"
                className="px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all"
                style={{ backgroundColor: '#161e35', color: '#94a3b8' }}
                onClick={onClose}
                disabled={submitting}
                onMouseEnter={(e) => !submitting && (e.currentTarget.style.color = '#fff')}
                onMouseLeave={(e) => !submitting && (e.currentTarget.style.color = '#94a3b8')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all inline-flex items-center gap-2"
                style={{ backgroundColor: '#ef4444', color: '#fff' }}
                disabled={submitting}
                onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#dc2626')}
                onMouseLeave={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#ef4444')}
              >
                {submitting ? (
                  <>
                    <span className="inline-block animate-spin">⏳</span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle"></i>
                    Update Details
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BankDetailsModal;
